// client/src/lib/rateLimitGate.ts
// 868mbzt38 — D2 + D3 + D4 (rate-limit gate)
// Single source of truth for the "wait out Retry-After" behaviour that lives
// behind every GET in the shared HTTP client. A 429 of a GET activates the
// gate for `retryAfterMs` (header → body.error.retryAfterMs → body.error.details.retryAfterMs
// → 30s default, capped at 5 min). The first activation fires every
// `onRateLimitActivated` listener exactly once, so a UI surface (RateLimitNotice)
// can show a single toast per activation. While the gate is active,
// `waitForGate(signal?)` blocks subsequent GETs without leaving TanStack's
// `status: 'error'`; mutations don't wait — they short-circuit with
// RateLimitedError with `msRemaining()` so the user sees why the action failed.
// The module keeps no React, no TanStack, no fetch dependencies (only
// AbortSignal): every call to `blockFor` and `isBlocked` is synchronous and
// pure, which keeps the test surface tiny.

type Listener = () => void;

let blockedUntil = 0;
const listeners = new Set<Listener>();

/**
 * Extend (or activate) the gate. Returns true only on the FIRST activation
 * (transition from inactive to active); listeners fire only on that edge.
 * A second 429 within the active window extends the deadline but does not
 * notify listeners again.
 */
export function blockFor(ms: number): boolean {
  const now = Date.now();
  const safeMs = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const next = Math.max(blockedUntil, now + safeMs);
  const wasActive = blockedUntil > now;
  blockedUntil = next;
  if (!wasActive) {
    for (const listener of listeners) {
      try {
        listener();
      } catch {
        // A listener failure must not derail the gate; swallow and keep going.
      }
    }
    return true;
  }
  return false;
}

export function isBlocked(): boolean {
  return blockedUntil > Date.now();
}

export function msRemaining(): number {
  const r = blockedUntil - Date.now();
  return r > 0 ? r : 0;
}

/**
 * Resolves once `now >= blockedUntil`. While waiting, polls every
 * `min(remainingMs, 100ms)` so an extension doesn't strand the caller. If
 * `signal` aborts while waiting, rejects with an `AbortError`-named error so
 * TanStack Query can recognise the cancellation.
 */
export function waitForGate(signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const abort = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
      }
      reject(new AbortError());
    };

    const check = (): void => {
      if (signal?.aborted) {
        if (timer !== null) {
          clearTimeout(timer);
        }
        reject(new AbortError());
        return;
      }
      const remaining = blockedUntil - Date.now();
      if (remaining <= 0) {
        resolve();
        return;
      }
      // Bound the wait so a long extension doesn't tie up the microtask queue.
      timer = setTimeout(check, Math.min(remaining, 100));
    };

    if (signal) {
      if (signal.aborted) {
        reject(new AbortError());
        return;
      }
      signal.addEventListener('abort', abort, { once: true });
    }

    check();
  });
}

export function onRateLimitActivated(listener: Listener): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

const DEFAULT_RETRY_AFTER_MS = 30_000;
const MAX_RETRY_AFTER_MS = 5 * 60_000;

/**
 * Resolve a `Retry-After` value, in milliseconds. Precedence (matches the
 * 868mbzt38 r2.1 addendum):
 *
 *  1. `Retry-After` header (seconds, integer).
 *  2. `bodyMs` — the caller has already extracted it from
 *     `body.retryAfterMs` (middleware path) or `body.error.details.retryAfterMs`
 *     (controller-throw path); this function does not know which.
 *  3. Default 30 s.
 *  4. Capped at 5 min so an adversary can't pin the whole app on a single
 *     huge header.
 */
export function resolveRetryAfterMs(
  headerValue: string | null | undefined,
  bodyMs: number | undefined | null,
): number {
  if (headerValue) {
    const parsed = Number.parseInt(headerValue, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.min(parsed * 1000, MAX_RETRY_AFTER_MS);
    }
  }
  if (typeof bodyMs === 'number' && Number.isFinite(bodyMs) && bodyMs > 0) {
    return Math.min(bodyMs, MAX_RETRY_AFTER_MS);
  }
  return DEFAULT_RETRY_AFTER_MS;
}

export class AbortError extends Error {
  constructor(message = 'aborted') {
    super(message);
    this.name = 'AbortError';
  }
}

export function __resetRateLimitGateForTesting(): void {
  blockedUntil = 0;
  listeners.clear();
}
